import { Icon } from '@fox/atoms';
import { FlexColumnSpaced, SpaceBetweenRow } from '@fox/layout';
import { useEffect, useRef, useState } from 'react';
import { DynamicEventCard } from '../dynamic-event-list/dynamic-event-card';
import { DynamicNewsCard } from '../dynamic-news-list/dynamic-news-card';
import { DynamicListCarouselContextProvider, useDynamicListCarouselContext } from './dynamic-list-context';
import {
	DynamicCardLeftFadeOverlay,
	DynamicCardRightFadeOverlay,
	DynamicCarouselCard,
	DynamicCarouselWrapper,
	DynamicListCarouselButtonWrapper
} from './shared';

const DynamicListCardComponents = {
	news: DynamicNewsCard,
	events: DynamicEventCard
};

export function DynamicListCarousel({
	items,
	fadeOutGradientColour,
	hideReadMore
}: {
	items: DynamicCarouselCard[];
	fadeOutGradientColour?: string;
	hideReadMore?: boolean;
}) {
	const widthRef = useRef<HTMLDivElement>(null);
	const [carouselWidth, setCarouselWidth] = useState<number>();

	useEffect(() => {
		const resizeHandler = () => {
			const width = widthRef?.current?.getBoundingClientRect()?.width;
			setCarouselWidth(width);
		};

		setTimeout(() => resizeHandler(), 500);

		window.addEventListener('resize', resizeHandler);
		return () => {
			window.removeEventListener('resize', resizeHandler);
		};
	}, [widthRef.current]);

	if (!items?.length) {
		return <></>;
	}

	return (
		<DynamicListCarouselContextProvider items={items} carouselWidth={carouselWidth}>
			<FlexColumnSpaced>
				<DynamicCarouselWrapper ref={widthRef}>
					<LeftFade fadeOutGradientColour={fadeOutGradientColour} />
					{items.map(item => {
						const Component = DynamicListCardComponents[item.type];
						return <Component key={item.id} {...item.props} hideReadMore={hideReadMore} />;
					})}
					<RightFade fadeOutGradientColour={fadeOutGradientColour} />
				</DynamicCarouselWrapper>
				<SpaceBetweenRow>
					<LeftButton />
					<RightButton />
				</SpaceBetweenRow>
			</FlexColumnSpaced>
		</DynamicListCarouselContextProvider>
	);
}

function LeftFade({ fadeOutGradientColour }: { fadeOutGradientColour?: string }) {
	const { carouselState } = useDynamicListCarouselContext();
	const visible = !!carouselState.partiallyVisibleItems.find(partialVisibleItem => partialVisibleItem < 0);
	return <DynamicCardLeftFadeOverlay visible={visible} fadeOutGradientColour={fadeOutGradientColour} />;
}

function RightFade({ fadeOutGradientColour }: { fadeOutGradientColour?: string }) {
	const { carouselState } = useDynamicListCarouselContext();
	const visible = !!carouselState.partiallyVisibleItems.find(partialVisibleItem => partialVisibleItem > 0);
	return <DynamicCardRightFadeOverlay visible={visible} fadeOutGradientColour={fadeOutGradientColour} />;
}

function LeftButton() {
	const { stepLeft, carouselState } = useDynamicListCarouselContext();
	return (
		<DynamicListCarouselButtonWrapper onClick={() => stepLeft()} visible={carouselState.showLeft}>
			<Icon iconName="ArrowBackIosNew" sx={{ fontSize: '2rem' }} customColor="black" />
		</DynamicListCarouselButtonWrapper>
	);
}

function RightButton() {
	const { stepRight, carouselState } = useDynamicListCarouselContext();
	return (
		<DynamicListCarouselButtonWrapper onClick={() => stepRight()} visible={carouselState.showRight}>
			<Icon iconName="ArrowForwardIos" sx={{ fontSize: '2rem' }} customColor="black" />
		</DynamicListCarouselButtonWrapper>
	);
}
